// Ügyfél-űrlap tiszta segédei (nem-komponens): form-értékek típusa, alapértékek, a @atlas/contracts
// create-sémája (a resolverhez) és a lokalizált zod-hibatérkép. Külön fájl a komponenstől, hogy a
// react-refresh (Fast Refresh) tiszta maradjon (a client-form.tsx CSAK a komponenst exportálja).
import { ClientCreateBodySchema } from '@atlas/contracts';
import { type ClientStatus } from '@atlas/shared';
import { type TFunction } from 'i18next';
import { z } from 'zod';

/** A form mező-értékei (string-alapú; a contract preprocess-e alakítja null-ra a write-oldalon). */
export interface ClientFormValues {
  name: string;
  email: string;
  phone: string;
  status: ClientStatus;
  autoBilling: boolean;
}

/** Üres (create) alapértékek. */
export const EMPTY_CLIENT_FORM: ClientFormValues = {
  name: '',
  email: '',
  phone: '',
  status: 'active',
  autoBilling: false,
};

/** A contract create-sémája a form-validáláshoz (a body-t a szerver újra-validálja + normalizálja). */
export const clientFormSchema = ClientCreateBodySchema;

/**
 * Lokalizált zod-hibatérkép: a zod-issue (mező-path + kód) → a `validation:clients.*` réteg üzenete.
 * Ismeretlen issue → a zod alapértelmezett üzenete (defaultError). Így a kliens-oldali validáció
 * ugyanabból a `validation` rétegből fordít, mint a szerver 422-terelése.
 */
export function buildClientErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'name') {
      if (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type) {
        return { message: t('validation:clients.name.required') };
      }
      if (issue.code === z.ZodIssueCode.too_big)
        return { message: t('validation:clients.name.max') };
    }
    if (field === 'email') {
      if (issue.code === z.ZodIssueCode.invalid_string) {
        return { message: t('validation:clients.email.invalid') };
      }
      if (issue.code === z.ZodIssueCode.too_big)
        return { message: t('validation:clients.email.max') };
    }
    if (field === 'phone' && issue.code === z.ZodIssueCode.too_big) {
      return { message: t('validation:clients.phone.max') };
    }
    // Ismeretlen issue a clients-mezőkön → lokalizált generikus üzenet; egyéb path → zod alapértelmezett.
    if (typeof field === 'string' && ['name', 'email', 'phone', 'status'].includes(field)) {
      return { message: t('validation:clients.generic') };
    }
    return { message: ctx.defaultError };
  };
}
